import React, { useState, useEffect, useContext, Suspense } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

// Lazy load the Sphere
const ThreeSphere = React.lazy(() => import('shared/components/ThreeSphere'));

function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useContext(AuthContext);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [inviteToken, setInviteToken] = useState('');
  const [shouldLoadSphere, setShouldLoadSphere] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Mobile detection
    setIsMobile(window.innerWidth <= 768);

    // Delay loading the ThreeSphere component
    const timeout = setTimeout(() => {
      setShouldLoadSphere(true);
    }, 200); // delay to avoid blocking synchronous UI render

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (location.state?.inviteToken) {
      setInviteToken(location.state.inviteToken);
    }
  }, [location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatusMessage('Logging in...');

    try {
      const response = await axios.post('/api/myteconstruction/auth/login', { email, password });
      const data = response.data;

      login({
        token: data.token,
        user_type: data.user_type,
        hasCompletedOnboarding: data.hasCompletedOnboarding || false,
      });

      setStatusMessage('Login successful!');

      if (inviteToken) {
        const confirmSwitch = window.confirm(
          "You have a pending invitation. Would you like to switch your organization now?"
        );
        if (confirmSwitch) {
          await axios.post(`/api/myteconstruction/invitations/accept?token=${inviteToken}`);
        }
      }

      if (data.user_type === 'Organization' && !data.hasCompletedOnboarding) {
        navigate('/myteconstruction/onboarding');
      } else if (data.user_type === 'Foreman') {
        navigate('/myteconstruction/foreman-dashboard');
      } else if (data.user_type === 'Project Manager') {
        navigate('/myteconstruction/project-manager-dashboard');
      } else {
        navigate('/myteconstruction/dashboard');
      }
    } catch (err) {
      setStatusMessage(`Error: ${err.response?.data?.error || err.message}`);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Sphere Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        {shouldLoadSphere && (
          <Suspense fallback={<div className="w-full h-full bg-black" />}>
            <ThreeSphere noAutoTransition zoomMultiplier={isMobile ? 2.5 : 3.5} />
          </Suspense>
        )}
      </div>

      {/* Login Form Container */}
      <div className="relative z-10 max-w-md w-full bg-gray-900 bg-opacity-100 sm:bg-opacity-90 p-4 sm:p-8 rounded-lg shadow-lg">
        <div className="mb-6 text-center">
          <img src="/LogoFile.png" alt="Myte Construction Logo" className="mx-auto w-24 mb-4" />
          <h2 className="text-4xl font-extrabold text-white">Login to Myte Construction</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-white font-medium">Email</label>
            <input
              type="email"
              className="w-full px-4 py-2 mt-1 rounded-md bg-gray-800 text-white"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-white font-medium">Password</label>
            <input
              type="password"
              className="w-full px-4 py-2 mt-1 rounded-md bg-gray-800 text-white"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-md font-semibold"
          >
            Login
          </button>
        </form>

        {statusMessage && (
          <div className="mt-4 text-center text-sm text-blue-200">
            {statusMessage}
          </div>
        )}

        <div className="mt-8 flex flex-col items-center space-y-4">
          <p className="text-sm text-gray-400">
            New here?{" "}
            <Link to="/myteconstruction/register" className="text-blue-400 hover:text-blue-300">
              Create an Account
            </Link>
          </p>
          <p>
            <Link
              to="/myteconstruction/forgot"
              className="text-blue-400 hover:text-blue-300 text-sm"
            >
              Forgot Password?
            </Link>
          </p>
          <Link
            to="/myteconstruction"
            className="inline-block px-6 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition"
          >
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
